import { forwardRef, useCallback, useMemo, useState } from "react";
import { tokens } from "@opendoor/tokens-studio/dist/bricks-next/tokens/Components";
import { isWeb, styled, Text as TamaguiText, useProps, View } from "@tamagui/core";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Close,
  Download,
  Edit,
  FavoritedFilled,
  FavoriteUnfilled,
  Filter,
  Menu,
  NearMe,
  PasswordHide,
  Share
} from "../../__generated__/icons";
import { getStateTokenValue } from "../../helpers/tokens";
import { createAnchorOrButtonFrame } from "../shared/anchorOrButton";
import { jsx } from "react/jsx-runtime";
const { iconButton: iconButtonTokens } = tokens, sharedFrameConfig = {
  name: "IconButton",
  userSelect: "none",
  cursor: "pointer",
  borderStyle: "solid",
  borderWidth: iconButtonTokens.borderWidth.default,
  borderRadius: iconButtonTokens.cornerRadius.default,
  alignItems: "center",
  justifyContent: "center",
  // Mobile safari sometimes adds a default horizontal padding to buttons
  padding: 0,
  animation: "linear",
  animateOnly: isWeb ? [
    "transform",
    "background-color",
    "border-color",
    "border-top-color",
    "border-left-color",
    "border-bottom-color",
    "border-right-color"
  ] : [
    "transform",
    "backgroundColor",
    "borderColor",
    "borderTopColor",
    "borderLeftColor",
    "borderBottomColor",
    "borderRightColor"
  ],
  focusVisibleStyle: {
    borderColor: iconButtonTokens.color.icon.border.state.focused.default
  },
  pressStyle: {
    scale: getStateTokenValue(iconButtonTokens.state.pressedScale)
  },
  variants: {
    variant: (variant, {
      props
    }) => props.disabled ? {
      backgroundColor: iconButtonTokens.color.icon.background.state.inactive[variant],
      borderColor: iconButtonTokens.color.icon.border.state.inactive[variant]
    } : {
      backgroundColor: iconButtonTokens.color.icon.background[variant],
      borderColor: iconButtonTokens.color.icon.border[variant],
      hoverStyle: {
        backgroundColor: iconButtonTokens.color.icon.background.state.hover[variant],
        borderColor: iconButtonTokens.color.icon.border.state.hover[variant]
      }
    },
    frameSize: (size) => ({
      width: iconButtonTokens.size[size],
      height: iconButtonTokens.size[size],
      ...size === "small" ? {
        backgroundColor: "$transparent",
        borderColor: "$transparent",
        hoverStyle: {
          borderColor: "$transparent"
        }
      } : {}
    }),
    disabled: {
      true: {
        pointerEvents: "none"
      }
    }
  }
}, AnchorFrame = styled(TamaguiText, {
  ...sharedFrameConfig,
  tag: "a",
  accessibilityRole: "link",
  textDecorationLine: "none",
  display: "flex",
  position: "relative"
}), ButtonFrame = styled(View, {
  ...sharedFrameConfig,
  tag: "button",
  accessibilityRole: "button"
}), AnchorOrButtonFrame = createAnchorOrButtonFrame({
  AnchorFrame,
  ButtonFrame
}), IconButton = forwardRef(function({ size, disabled, icon, ...rest }, ref) {
  const { variant = "default" } = useProps(rest), iconColor = disabled ? iconButtonTokens.color.icon.content.state.inactive.default : icon.color ?? iconButtonTokens.color.icon.content[variant], iconSize = icon.size ?? (size === "large" ? 24 : 20);
  return /* @__PURE__ */ jsx(
    AnchorOrButtonFrame,
    {
      disabled,
      "aria-disabled": disabled,
      focusable: !disabled,
      ...rest,
      variant,
      frameSize: size,
      ref,
      children: /* @__PURE__ */ jsx(icon.Component, { "aria-label": icon.label, color: iconColor, size: iconSize })
    }
  );
}), CloseButton = forwardRef(function({ iconLabel, iconSize, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    IconButton,
    {
      icon: { Component: Close, label: iconLabel ?? "close", size: iconSize },
      ...props,
      ref
    }
  );
}), MenuButton = forwardRef(function({ iconLabel, iconSize, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    IconButton,
    {
      icon: { Component: Menu, label: iconLabel ?? "menu", size: iconSize },
      ...props,
      ref
    }
  );
}), ChevronBackButton = forwardRef(function({ iconLabel, iconSize, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    IconButton,
    {
      icon: { Component: ChevronLeft, label: iconLabel ?? "back", size: iconSize },
      ...props,
      ref
    }
  );
}), ChevronForwardButton = forwardRef(function({ iconLabel, iconSize, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    IconButton,
    {
      icon: { Component: ChevronRight, label: iconLabel ?? "forward", size: iconSize },
      ...props,
      ref
    }
  );
}), ChevronUpButton = forwardRef(function({ iconLabel, iconSize, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    IconButton,
    {
      icon: { Component: ChevronUp, label: iconLabel ?? "up", size: iconSize },
      ...props,
      ref
    }
  );
}), ChevronDownButton = forwardRef(function({ iconLabel, iconSize, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    IconButton,
    {
      icon: { Component: ChevronDown, label: iconLabel ?? "down", size: iconSize },
      ...props,
      ref
    }
  );
}), EditButton = forwardRef(function({ iconLabel, iconSize, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    IconButton,
    {
      icon: { Component: Edit, label: iconLabel ?? "edit", size: iconSize },
      ...props,
      ref
    }
  );
}), DownloadButton = forwardRef(function({ iconLabel, iconSize, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    IconButton,
    {
      icon: {
        Component: Download,
        label: iconLabel ?? "download",
        // Medium size uses 24px icon
        size: iconSize ?? (props.size === "small" ? 20 : 24)
      },
      ...props,
      ref
    }
  );
}), FilterButton = forwardRef(function({ iconLabel, iconSize, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    IconButton,
    {
      icon: { Component: Filter, label: iconLabel ?? "filter", size: iconSize },
      ...props,
      ref
    }
  );
}), FavoriteButton = forwardRef(function({
  iconLabel,
  iconSize,
  defaultFavorited,
  isFavorited,
  onPress,
  ...props
}, ref) {
  const [internalIsFavorited, setInternalIsFavorited] = useState(defaultFavorited ?? !1), isControlledComponent = typeof isFavorited < "u", favorited = isControlledComponent ? isFavorited : internalIsFavorited, handlePress = useCallback(
    (e) => (!isControlledComponent && setInternalIsFavorited((prev) => !prev), onPress?.(e)),
    [isControlledComponent, onPress]
  ), icon = useMemo(() => favorited ? {
    Component: FavoritedFilled,
    label: iconLabel ?? "unfavorite",
    size: iconSize,
    color: iconButtonTokens.color.icon.content.favorite
  } : {
    Component: FavoriteUnfilled,
    label: iconLabel ?? "favorite",
    size: iconSize
  }, [favorited, iconLabel, iconSize]);
  return /* @__PURE__ */ jsx(
    IconButton,
    {
      icon,
      value: favorited ? "false" : "true",
      onPress: handlePress,
      ...props,
      ref
    }
  );
}), NearMeButton = forwardRef(function({ iconLabel, iconSize, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    IconButton,
    {
      icon: { Component: NearMe, label: iconLabel ?? "near me", size: iconSize },
      ...props,
      ref
    }
  );
}), HideButton = forwardRef(function({ iconLabel, iconSize, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    IconButton,
    {
      icon: { Component: PasswordHide, label: iconLabel ?? "hide", size: iconSize },
      ...props,
      ref
    }
  );
}), ShareButton = forwardRef(function({ iconLabel, iconSize, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    IconButton,
    {
      icon: {
        Component: Share,
        label: iconLabel ?? "share",
        // Medium size uses 24px icon
        size: iconSize ?? (props.size === "small" ? 20 : 24)
      },
      ...props,
      ref
    }
  );
});
export {
  ChevronBackButton,
  ChevronDownButton,
  ChevronForwardButton,
  ChevronUpButton,
  CloseButton,
  DownloadButton,
  EditButton,
  FavoriteButton,
  FilterButton,
  HideButton,
  IconButton,
  MenuButton,
  NearMeButton,
  ShareButton
};
